import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useApiRequest from '../../../shared/hooks/useApiRequest';
import {
  EMPTY_STORE_INTELLIGENCE_SUMMARY,
  generateStoreIntelligenceSummary,
  generateOfferDraftFromStoreIntelligenceSuggestion,
  getStoreIntelligenceHistory,
  getStoreIntelligenceLatest,
  normalizeStoreIntelligenceHistory,
  updateStoreIntelligenceSuggestionStatus
} from '../api/storeIntelligenceApi';
import useAnalyticsResource from './useAnalyticsResource';

const EMPTY_HISTORY = Object.freeze({
  storeUuid: null,
  limit: 0,
  summaries: [],
  isEmpty: true
});

const DEFAULT_ERROR_MESSAGE = 'Unable to complete Store Intelligence request.';
const GENERATION_POLL_INTERVAL_MS = 3_000;
const GENERATION_POLL_ATTEMPTS = 20;
const GENERATION_STATUS_IN_PROGRESS = 'IN_PROGRESS';
const GENERATION_STATUS_FAILED = 'FAILED';
const SUGGESTION_STATUS_GENERATING_OFFER = 'GENERATING_OFFER';
const SUGGESTION_STATUS_USED_FOR_OFFER = 'USED_FOR_OFFER';

const wait = (delayMs) => new Promise((resolve) => {
  setTimeout(resolve, delayMs);
});

const isGenerationAlreadyRunning = (error) => (
  error?.status === 409
  || error?.data?.data?.statusCode === 409
  || error?.data?.statusCode === 409
);

const useStoreIntelligence = ({
  request,
  storeToken,
  refreshSignal,
  historyLimit = 10,
  loadHistory = false,
  onToast
} = {}) => {
  const enabled = Boolean(request && storeToken);
  const onToastRef = useRef(onToast);
  onToastRef.current = onToast;
  const generationInFlightRef = useRef(null);

  const {
    execute: executeMutation,
    loading: mutationLoading,
    error: mutationError
  } = useApiRequest();

  const [historyOverride, setHistoryOverride] = useState(null);
  const [summaryOverride, setSummaryOverride] = useState(null);
  const [isGeneratingSummary, setIsGeneratingSummary] = useState(false);

  const latestFetcher = useCallback(() => getStoreIntelligenceLatest(request), [request]);
  const latestResource = useAnalyticsResource(
    enabled,
    latestFetcher,
    'useStoreIntelligence.latest',
    refreshSignal
  );

  const historyFetcher = useCallback(() => getStoreIntelligenceHistory(request, {
    limit: historyLimit
  }), [historyLimit, request]);
  const historyResource = useAnalyticsResource(
    enabled && loadHistory,
    historyFetcher,
    'useStoreIntelligence.history',
    `${refreshSignal}:${historyLimit}`
  );

  useEffect(() => {
    if (latestResource.data?.available) {
      setSummaryOverride(null);
    }
  }, [latestResource.data]);

  useEffect(() => {
    setSummaryOverride(null);
    setHistoryOverride(null);
  }, [storeToken]);

  const summary = summaryOverride || latestResource.data || EMPTY_STORE_INTELLIGENCE_SUMMARY;
  const history = historyOverride || historyResource.data || EMPTY_HISTORY;
  const isEmpty = !summary.available;

  const replaceSuggestion = useCallback((updatedSuggestion) => {
    if (!updatedSuggestion?.id) {
      return;
    }

    setSummaryOverride((previousSummary) => {
      const baseSummary = previousSummary || latestResource.data;
      if (!baseSummary?.strategySuggestions?.length) {
        return previousSummary;
      }
      return {
        ...baseSummary,
        strategySuggestions: baseSummary.strategySuggestions.map((suggestion) => (
          String(suggestion.id) === String(updatedSuggestion.id)
            ? { ...suggestion, ...updatedSuggestion }
            : suggestion
        ))
      };
    });

    setHistoryOverride((previousHistory) => {
      if (!previousHistory?.summaries?.length) {
        return previousHistory;
      }
      return {
        ...previousHistory,
        summaries: previousHistory.summaries.map((item) => ({
          ...item,
          strategySuggestions: item.strategySuggestions.map((suggestion) => (
            String(suggestion.id) === String(updatedSuggestion.id)
              ? { ...suggestion, ...updatedSuggestion }
              : suggestion
          ))
        }))
      };
    });
    latestResource.refetch().catch(() => null);
  }, [latestResource]);

  const updateSuggestionStatus = useCallback(async (suggestionId, payload = {}) => executeMutation(async () => {
    if (!enabled) {
      throw new Error('Missing authenticated request context');
    }
    const updatedSuggestion = await updateStoreIntelligenceSuggestionStatus(request, suggestionId, payload);
    replaceSuggestion(updatedSuggestion);
    return updatedSuggestion;
  }, {
    logModule: 'useStoreIntelligence.suggestionStatus',
    onSuccess: () => {
      onToastRef.current?.({ message: 'Store intelligence suggestion updated.', variant: 'success' });
    },
    onError: (error) => {
      onToastRef.current?.({
        message: error?.data?.message || error?.message || DEFAULT_ERROR_MESSAGE,
        variant: 'error'
      });
    }
  }), [enabled, executeMutation, replaceSuggestion, request]);

  const markSuggestionViewed = useCallback((suggestionId, reason) => (
    updateSuggestionStatus(suggestionId, { status: 'VIEWED', reason })
  ), [updateSuggestionStatus]);

  const dismissSuggestion = useCallback((suggestionId, reason) => (
    updateSuggestionStatus(suggestionId, { status: 'DISMISSED', reason })
  ), [updateSuggestionStatus]);

  // Summary generation runs on a backend worker (202 + generationStatus IN_PROGRESS). Poll
  // /summary/latest until the run settles. NOTE: `available` can already be true from a prior
  // week's summary, so we key off generationStatus, not availability.
  const pollLatestGeneratedSummary = useCallback(async () => {
    for (let attempt = 0; attempt < GENERATION_POLL_ATTEMPTS; attempt += 1) {
      await wait(GENERATION_POLL_INTERVAL_MS);
      const latest = await getStoreIntelligenceLatest(request);
      const status = latest?.generationStatus;
      if (status === GENERATION_STATUS_FAILED) {
        throw new Error(latest?.generationMessage || 'Store intelligence summary generation failed.');
      }
      if (status !== GENERATION_STATUS_IN_PROGRESS && latest?.available) {
        return latest;
      }
    }
    throw new Error('Store intelligence generation is still running. Please refresh in a moment.');
  }, [request]);

  // Draft-from-insight also runs on a worker (202 + suggestion GENERATING_OFFER). Poll the
  // suggestion status: USED_FOR_OFFER = success; any other terminal status = generation failed.
  const pollSuggestionDraft = useCallback(async (suggestionId) => {
    for (let attempt = 0; attempt < GENERATION_POLL_ATTEMPTS; attempt += 1) {
      await wait(GENERATION_POLL_INTERVAL_MS);
      const latest = await getStoreIntelligenceLatest(request);
      const suggestion = (latest?.strategySuggestions || []).find(
        (item) => String(item.id) === String(suggestionId)
      );
      const status = suggestion?.status;
      if (status === SUGGESTION_STATUS_USED_FOR_OFFER) {
        return suggestion;
      }
      if (status && status !== SUGGESTION_STATUS_GENERATING_OFFER) {
        throw new Error('AI offer draft generation failed. Please try again.');
      }
    }
    throw new Error('AI offer draft is still generating. Please refresh in a moment.');
  }, [request]);

  const generateSummary = useCallback(async () => {
    if (generationInFlightRef.current) {
      return generationInFlightRef.current;
    }
    setIsGeneratingSummary(true);
    const generationPromise = (async () => {
      try {
        return await executeMutation(async () => {
          if (!enabled) {
            throw new Error('Missing authenticated request context');
          }
          let generatedSummary;
          try {
            generatedSummary = await generateStoreIntelligenceSummary(request);
          } catch (error) {
            if (!isGenerationAlreadyRunning(error)) {
              throw error;
            }
            generatedSummary = { generationStatus: GENERATION_STATUS_IN_PROGRESS };
          }
          // 202 ACCEPTED → generation still running on the worker; poll until it settles.
          if (generatedSummary?.generationStatus === GENERATION_STATUS_IN_PROGRESS) {
            generatedSummary = await pollLatestGeneratedSummary();
          }
          setSummaryOverride(generatedSummary);
          if (loadHistory) {
            const nextHistory = await getStoreIntelligenceHistory(request, { limit: historyLimit });
            setHistoryOverride(normalizeStoreIntelligenceHistory(nextHistory));
          }
          return generatedSummary;
        }, {
          logModule: 'useStoreIntelligence.generateSummary',
          onSuccess: () => {
            onToastRef.current?.({ message: 'Store intelligence summary generated.', variant: 'success' });
          },
          onError: (error) => {
            onToastRef.current?.({
              message: error?.data?.message || error?.message || DEFAULT_ERROR_MESSAGE,
              variant: 'error'
            });
          }
        });
      } finally {
        setIsGeneratingSummary(false);
        generationInFlightRef.current = null;
      }
    })();
    generationInFlightRef.current = generationPromise;
    return generationPromise;
  }, [enabled, executeMutation, historyLimit, loadHistory, pollLatestGeneratedSummary, request]);

  const generateOfferDraftFromSuggestion = useCallback(async (
    suggestionId,
    payload = {}
  ) => executeMutation(async () => {
    if (!enabled) {
      throw new Error('Missing authenticated request context');
    }
    // 202 ACCEPTED: the backend marks the suggestion GENERATING_OFFER and generates the draft
    // on a worker. The POST body is not authoritative for the draft, so we discard it and poll
    // the suggestion status. Refetch once to reflect GENERATING_OFFER, then poll to completion.
    await generateOfferDraftFromStoreIntelligenceSuggestion(request, suggestionId, payload);
    await latestResource.refetch();
    const completedSuggestion = await pollSuggestionDraft(suggestionId);
    replaceSuggestion(completedSuggestion);
    if (loadHistory) {
      const nextHistory = await getStoreIntelligenceHistory(request, { limit: historyLimit });
      setHistoryOverride(normalizeStoreIntelligenceHistory(nextHistory));
    }
    return completedSuggestion;
  }, {
    logModule: 'useStoreIntelligence.generateOfferDraft',
    onSuccess: () => {
      onToastRef.current?.({ message: 'AI offer draft created — view it in AI Studio.', variant: 'success' });
    },
    onError: (error) => {
      onToastRef.current?.({
        message: error?.data?.message || error?.message || DEFAULT_ERROR_MESSAGE,
        variant: 'error'
      });
    }
  }), [
    enabled,
    executeMutation,
    historyLimit,
    latestResource,
    loadHistory,
    pollSuggestionDraft,
    replaceSuggestion,
    request
  ]);

  // Manual-generation cooldown (weekly) surfaced by the backend on the summary,
  // so the "Generate summary" button can always show either an enabled state or a
  // countdown to when it unlocks — mirroring the AI-offer generation quota.
  const canGenerateSummary = summary.canGenerate !== false;
  const generationCooldownSeconds = Number(summary.generationCooldownSeconds) || 0;
  const generationRetryAtMs = generationCooldownSeconds > 0
    ? Date.now() + generationCooldownSeconds * 1000
    : null;

  return useMemo(() => ({
    summary,
    history,
    suggestions: summary.strategySuggestions,
    moduleSummaries: summary.moduleSummaries,
    isEmpty,
    isLoading: latestResource.isLoading,
    isRefreshing: latestResource.isRefreshing,
    isHistoryLoading: historyResource.isLoading,
    isMutating: mutationLoading,
    isGeneratingSummary,
    canGenerateSummary,
    generationRetryAtMs,
    isError: latestResource.isError,
    error: latestResource.error || mutationError,
    refetchLatest: latestResource.refetch,
    refetchHistory: historyResource.refetch,
    generateSummary,
    updateSuggestionStatus,
    markSuggestionViewed,
    dismissSuggestion,
    generateOfferDraftFromSuggestion
  }), [
    canGenerateSummary,
    generationRetryAtMs,
    dismissSuggestion,
    generateSummary,
    generateOfferDraftFromSuggestion,
    history,
    historyResource.isLoading,
    historyResource.refetch,
    isEmpty,
    isGeneratingSummary,
    latestResource.error,
    latestResource.isError,
    latestResource.isLoading,
    latestResource.isRefreshing,
    latestResource.refetch,
    markSuggestionViewed,
    mutationError,
    mutationLoading,
    summary,
    updateSuggestionStatus
  ]);
};

export default useStoreIntelligence;
